<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Announcements', to: ROUTES.ADMIN.ANNOUNCEMENTS.ANNOUNCEMENT_LIST }
          ]"
        />
        <router-link
          :to="ROUTES.ADMIN.ANNOUNCEMENTS.ANNOUNCEMENT_CREATE"
          class="inline-flex items-center gap-x-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white
            shadow-sm hover:bg-purple-700 disabled:pointer-events-none disabled:opacity-50"
        >
          Create Announcement
        </router-link>
      </div>

      <!-- Announcements Table Card -->
      <div class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <PaginatedTableView
          :fetchData="fetchAnnouncements"
          searchable-fields="Message"
        >
          <template #table-head>
            <TableCell>Message</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Dismissible</TableCell>
            <TableCell>Start Date</TableCell>
            <TableCell>End Date</TableCell>
            <TableCell>Actions</TableCell>
          </template>
          <template #table-row="{ items }">
            <TableRow
              v-for="announcement in items as SuperUserAnnouncement[]"
              :key="announcement.uuid"
            >
              <TableCell class="max-w-md">
                <div class="truncate">
                  {{ announcement.message }}
                </div>
              </TableCell>
              <TableCell>
                <span
                  :class="[
                    'inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium',
                    getTypeColorClasses(announcement.message_type)
                  ]"
                >
                  {{ announcement.message_type }}
                </span>
              </TableCell>
              <TableCell>
                <span
                  :class="[
                    'inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium',
                    announcement.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  ]"
                >
                  {{ announcement.is_active ? 'Active' : 'Inactive' }}
                </span>
              </TableCell>
              <TableCell>
                {{ announcement.dismissible ? 'Yes' : 'No' }}
              </TableCell>
              <TableCell>
                {{ announcement.start_date ? formatDate(announcement.start_date) : 'N/A' }}
              </TableCell>
              <TableCell>
                {{ announcement.end_date ? formatDate(announcement.end_date) : 'N/A' }}
              </TableCell>
              <TableCell>
                <TableOptionsButton
                  :options="[
                    {
                      label: 'Edit',
                      to: ROUTES.ADMIN.ANNOUNCEMENTS.ANNOUNCEMENT_DETAIL.replace(
                        ':uuid',
                        announcement.uuid
                      )
                    }
                  ]"
                />
              </TableCell>
            </TableRow>
          </template>
        </PaginatedTableView>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { TableRow, TableCell } from '@/components/ui/table'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import { AnnouncementsService } from '@/types/generated/services.gen'
  import type { SuperUserAnnouncement } from '@/types/generated/types.gen'

  const fetchAnnouncements = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<SuperUserAnnouncement>> => {
    const response = await AnnouncementsService.announcementsList({
      limit: options?.limit,
      offset: options?.offset
    })

    return {
      results: (response.results as unknown as SuperUserAnnouncement[]) || [],
      next: response.next ?? null,
      previous: response.previous ?? null,
      count: response.count
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTypeColorClasses = (type: string) => {
    switch (type) {
      case 'ERROR':
        return 'bg-red-100 text-red-800'
      case 'WARNING':
        return 'bg-yellow-100 text-yellow-800'
      case 'SUCCESS':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-blue-100 text-blue-800'
    }
  }
</script>
